import React, { useState, useCallback } from 'react'
import PaymentCardsList from '../PaymentCardsList/PaymentCardsList'
import { t } from 'translation'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { format, addDays } from 'date-fns'
import * as Yup from 'yup'
import { Formik, Form, Field } from 'formik'
import { connect } from 'react-redux'
import AuthErrorBox from 'components/Auth/AuthErrorBox'
import { authFail, resetAuthError, authRefreshToken } from 'store/actions'
import LoadingButton from 'components/ui/LoadingButton/LoadingButton'
import { paymentApi } from 'api'

const paymentInfoSchema = Yup.object().shape({
  cardholderName: Yup.string().required('CARDHOLDER_IS_REQUIRED'),
})

const SubscriptionPatientStepTwo = ({
  authFail,
  authError,
  freeDays,
  currency,
  price,
  resetAuthError,
  authRefreshToken,
}) => {
  const [date] = useState(format(addDays(new Date(), freeDays), 'd/M/yy'))
  const [cardError, setCardError] = useState(true)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [cardTouched, setCardTouched] = useState(false)

  const stripe = useStripe()
  const elements = useElements()

  const stripeSubmitHandler = useCallback(
    async ({ cardholderName }) => {
      setIsLoading(true)

      try {
        const cardElement = elements.getElement(CardElement)
        const { paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: cardholderName,
            // TODO: Confirm no need for phone/billing
            // address: {
            //   line1: billing.address.line1,
            //   line2: billing.address.line2,
            //   city: billing.address.city,
            //   state: billing.address.state,
            //   postal_code: billing.address.zipcode,
            //   country: 'US',
            // },
          },
        })

        const { result, error } = await paymentApi.createPatientSubscription({
          cardholderName,
          paymentMethodId: paymentMethod.id,
        })

        if (error) throw Error(error?.message)

        await authRefreshToken()

        if (window.cordova) {
          return setTimeout(() => {
            window.location.hash = '#/welcome'
            location.reload()
          }, 6000)
        }

        const redirectPath = `/welcome`

        return setTimeout(() => window.location.replace(redirectPath), 6000)
      } catch (error) {
        console.log(
          'Error creating subscription: ',
          error?.error_description || error?.message || error
        )
        setIsLoading(false)
        return authFail(error)
      }
    },
    [cardError]
  )

  return (
    <Formik
      initialValues={{ cardholderName: '' }}
      validationSchema={paymentInfoSchema}
      onSubmit={(values) => {
        if (cardError) return
        stripeSubmitHandler(values)
      }}
    >
      {({ errors, handleChange, submitForm, status, setStatus, touched }) => (
        <Form className="auth_block">
          <h1 className="section_title v4_mod color_mod offset_v4_mod decor_mod">
            {t('signup.PAYMENT_INFO')}
          </h1>
          {!success && (
            <AuthErrorBox
              formFields={['prescriberCode', 'email']}
              formTouched={touched}
              formErrors={errors}
              apiResponseError={authError || ''}
              resetApiResponseError={resetAuthError}
            />
          )}
          <div className="payment_info decor_mod offset_mod">
            <ul className="payment_info_list decor_mod offset_mod">
              {freeDays > 0 && (
                <li className="payment_info_list_item active_mod">
                  <span className="payment_info_list_item_title">
                    {t('signup.TRY_TODAY')}
                  </span>
                  <span className="payment_info_list_item_subtitle">
                    {t('signup.FREE_TRIAL_DAYS', { days: freeDays })}
                  </span>
                </li>
              )}
              <li
                className={`payment_info_list_item ${freeDays > 0 ? '' : 'no_free_days'}`}
              >
                <span className="payment_info_list_item_title">
                  {freeDays > 0
                    ? `${t('signup.BEGINNING_ON')} ${date}`
                    : t('subscription.BEGINNING_TODAY')}
                </span>
                <span className="payment_info_list_item_subtitle">
                  {currency === 'USD' ? '$' : null}
                  {price}
                  {currency === 'EUR' ? '€' : null}
                </span>
              </li>
            </ul>
            <div className="auth_block_note limit_mod v1_mod">
              {freeDays > 0
                ? `${t('signup.CANCEL_ANYTIME_1')} ${date} ${t(
                    'signup.CANCEL_ANYTIME_2'
                  )}`
                : t('subscription.CANCEL_ANYTIME')}
            </div>
          </div>

          <PaymentCardsList />
          <div className="auth_block_input_w_list decor_mod offset_mod">
            <ul className="auth_block_input_list">
              <li className="auth_block_input_list_item">
                <dl className="form_cell form_cell_v1_mod">
                  <dt className="form_cell_title form_v3_mod hline_hide_mod">
                    <label htmlFor="login_mail">{t(`signup.NAME_CARDHOLDER`)}</label>
                  </dt>
                  <dd className="form_field_wrap form_v3_mod">
                    <Field
                      className={`form_field v4_mod ${
                        errors.cardholderName ? 'error' : ''
                      }`}
                      type="text"
                      name="cardholderName"
                      id="cardholder_name"
                      disabled={isLoading}
                      placeholder={t(`signup.NAME_CARDHOLDER`)}
                      onChange={(event) => (resetAuthError(), handleChange(event))}
                    />
                    {/* <input
                        id="cardholder_name"
                        name="cardholderName"
                        type="text"
                        placeholder={t(`signup.NAME_CARDHOLDER`)}
                        className="form_field v4_mod"
                      /> */}
                  </dd>
                </dl>
              </li>
              <li className="auth_block_input_list_item limitColClass">
                <dl className="form_cell form_cell_v1_mod">
                  <dt className="form_cell_title form_v3_mod hline_hide_mod">
                    <label htmlFor="login_mail">{t(`signup.CREDIT_CARD_NUMBER`)}</label>
                  </dt>
                  <dd className="form_field_wrap form_v3_mod">
                    <CardElement
                      id="credit_card_number"
                      placeholder="signup.CREDIT_CARD_NUMBER"
                      className={`form_field v4_mod ${
                        (status === 'clicked' || cardTouched) && cardError ? 'error' : ''
                      } stripe-element`}
                      onChange={({ error }) => (
                        setCardTouched(true), setCardError(!!error)
                      )}
                      options={{
                        hideIcon: true,
                        hidePostalCode: true,
                        style: {
                          base: {
                            fontSize: '1rem',
                            fontFamily: 'Bariol, sans-serif',
                            fontWight: '400',
                            fontSmoothing: 'antialiased',
                            color: 'rgb(2,2,2)',
                            '::placeholder': {
                              color: '#818181',
                            },
                          },
                        },
                      }}
                    />
                  </dd>
                </dl>
              </li>
            </ul>
          </div>
          <div className="auth_block_note offset_v1_mod">
            {freeDays > 0
              ? `${t('signup.FACTURATION_DESCR_1')}
                ${date}
                ${t('signup.FACTURATION_DESCR_2', {
                  price,
                  usd: currency === 'USD' ? '$' : null,
                  eur: currency === 'EUR' ? '€' : null,
                })}`
              : t('subscription.FACTURATION_DESCR', {
                  price,
                  usd: currency === 'USD' ? '$' : null,
                  eur: currency === 'EUR' ? '€' : null,
                })}
          </div>
          <div className="btn_wrap center_lvl_mod">
            <LoadingButton
              className="btn_base_v1"
              isLoading={isLoading}
              onClick={(e) => {
                e.preventDefault()

                setStatus('clicked')
                submitForm()
              }}
              disabled={success || !stripe || !elements}
            >
              {t('subscription.SUBSCRIBE')}
            </LoadingButton>
          </div>
        </Form>
      )}
    </Formik>
  )
}

const mapStateToProps = ({ auth: { error, attributes } }) => ({
  alwaysOnWelcomeVideo: attributes?.permissions?.features?.alwaysOnWelcomeVideo,
  authError: error,
})

const willMapDispatchToProps = {
  authFail,
  resetAuthError,
  authRefreshToken,
}

export default connect(
  mapStateToProps,
  willMapDispatchToProps
)(SubscriptionPatientStepTwo)
